/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
} from "@mui/material";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Group an array of member objects by their section (default "1")
 */
function groupMembersBySection(members) {
  const grouped = {};
  members.forEach((member) => {
    const section = member.section || "1";
    if (!grouped[section]) {
      grouped[section] = [];
    }
    grouped[section].push(member);
  });
  return grouped;
}

/**
 * PennantTeams component fetches and displays teams for a given pennant.
 * Renders the Teams heading only if there is team data.
 *
 * Props:
 * - pennantId (string): The ID of the pennant document.
 */
export default function PennantTeams({ pennantId }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchTeams() {
      try {
        const teamsSnapshot = await getDocs(
          collection(db, "Pennants", pennantId, "teams")
        );
        const teamsData = [];
        teamsSnapshot.forEach((docSnap) => {
          teamsData.push({ id: docSnap.id, ...docSnap.data() });
        });
        setTeams(teamsData);
      } catch (error) {
        console.error("Error fetching teams:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchTeams();
  }, [pennantId]);

  if (loading) {
    return <Typography>Loading teams...</Typography>;
  }

  // Only render if there is team data.
  if (teams.length === 0) {
    return null;
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h5" gutterBottom>
        Teams
      </Typography>
      {teams.map((team) => {
        // Convert team.members (an object) to an array.
        const membersArray = Object.values(team.members || {});
        const groupedMembers = groupMembersBySection(membersArray);
        const sortedSectionKeys = Object.keys(groupedMembers).sort(
          (a, b) => Number(a) - Number(b)
        );
        return (
          <Box
            key={team.id}
            sx={{
              mb: 3,
              p: 1,
              border: "1px solid #ccc",
              borderRadius: 1,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography variant="h6">
              {team.name || "Unnamed Team"}
            </Typography>
            {sortedSectionKeys.map((section) => (
              <Box key={section} sx={{ ml: 2, mt: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    bgcolor: "#d0d0ff",
                    p: 0.5,
                    borderRadius: 1,
                    display: "inline-block",
                  }}
                >
                  Section: {section}
                </Typography>
                <Paper sx={{ mt: 1, p: 1 }}>
                  <List dense>
                    {groupedMembers[section].map((member) => (
                      <ListItem key={member.id} sx={{ pl: 2 }}>
                        <ListItemText
                          primary={`${member.firstName} ${member.lastName}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
